import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, ProgressBar } from 'react-bootstrap';
import { apiErrorText } from 'Utils/errorText';
import usePolling from 'TestDataManagement/usePolling';
import { parseSeeds, uniqueSeeds, fmtNum, fmtWhen } from 'TestDataManagement/tdmUtils';
import { startGraphTest, getGraphTest } from './dataGraphApi';
import { IsolationReport, ReportWarnings, reportNoun } from './ObjectReport';

const POLL_MS = 3000;
const LIST_LIMIT = 20;
/** The platform refuses an ID in list longer than this (400); it tests the first TESTED_IDS of it. */
export const TEST_MAX_IDS = 100000;
const TESTED_IDS = 100;
const SAMPLE_RECORDS = 10;

export const TEST_MODES = [['ANY', 'Any'], ['WHERE', 'Where clause'], ['SEEDS', 'ID in']];
const MODE_LABEL = Object.fromEntries(TEST_MODES);

const WHERE = {
  salesforce: { label: 'SOQL where', placeholder: "MailingState = 'MA'" },
  mongodb: { label: 'Filter', placeholder: '{"state": "MA"}' },
  sql: { label: 'Where', placeholder: "state = 'MA'" },
};
export const whereLanguage = (engine) => {
  const e = String(engine || '').toLowerCase();
  if (e === 'salesforce') return WHERE.salesforce;
  return e === 'mongodb' ? WHERE.mongodb : WHERE.sql;
};

/** Why Test cannot run yet, or null. The first sentence is the platform's own 400. */
export const testProblem = (graph, form, seeds) => {
  if (!graph || !graph.keyTable) return 'Set the graph\'s key table first — the test starts where a subset would.';
  if (form.mode === 'WHERE' && !form.where.trim()) return 'Type a where clause to test.';
  if (form.mode === 'SEEDS' && seeds.ids.length === 0) return 'List at least one id to test.';
  if (form.mode === 'SEEDS' && seeds.ids.length > TEST_MAX_IDS) return `At most ${fmtNum(TEST_MAX_IDS)} ids.`;
  return null;
};

const requestLabel = (req) => {
  if (!req || !req.mode) return null;
  if (req.mode === 'WHERE') return `${MODE_LABEL.WHERE}: ${req.where || ''}`;
  if (req.mode === 'SEEDS') return `${MODE_LABEL.SEEDS}: ${fmtNum(req.seedsCount)} id${req.seedsCount === 1 ? '' : 's'}`;
  return MODE_LABEL[req.mode] || req.mode;
};

// The status bar (2026-11-02). While RUNNING the platform returns no report, only the worker's
// `progress`: { phase, currentItem, tablesDone, tablesTotal, rows, percent }, any key absent until
// the worker reports it. The report arrives with the first read after the test has finished.
const PHASE_LABEL = { SEEDS: 'Finding seed records', EXTRACT: 'Walking the graph' };
export const phaseLabel = (phase) => (phase ? PHASE_LABEL[phase] || String(phase) : 'Testing…');

const known = (v) => v != null && v !== '' && Number.isFinite(Number(v));

/** 0..100, or null when the worker has not said (the bar is indeterminate then). */
export const progressPercent = (p) => (p && known(p.percent) ? Math.min(100, Math.max(0, Math.round(Number(p.percent)))) : null);

/** "0:42", "12:05", "1:02:03" */
export const fmtElapsed = (ms) => {
  const s = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  const pad = (x) => String(x).padStart(2, '0');
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}:${pad(m)}:${pad(s % 60)}` : `${m}:${pad(s % 60)}`;
};

/** "12 of 40 tables · 1,234 rows · Account · 0:42 elapsed", with what is not known left out. */
export const progressDetail = (progress, { noun = 'Table', startedAt, now = Date.now() } = {}) => {
  const p = progress || {};
  const n = String(noun).toLowerCase();
  const parts = [];
  if (known(p.tablesDone) && known(p.tablesTotal)) {
    parts.push(`${fmtNum(p.tablesDone)} of ${fmtNum(p.tablesTotal)} ${n}${Number(p.tablesTotal) === 1 ? '' : 's'}`);
  } else if (known(p.tablesDone)) {
    parts.push(`${fmtNum(p.tablesDone)} ${n}${Number(p.tablesDone) === 1 ? '' : 's'}`);
  }
  if (known(p.rows)) parts.push(`${fmtNum(p.rows)} row${Number(p.rows) === 1 ? '' : 's'}`);
  if (p.currentItem) parts.push(String(p.currentItem));
  const started = startedAt ? new Date(startedAt).getTime() : NaN;
  if (Number.isFinite(started)) parts.push(`${fmtElapsed(now - started)} elapsed`);
  return parts.join(' · ');
};

// Re-renders once a second, so the elapsed clock ticks between the 3 s reads.
const useTick = (active) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [active]);
};

// A label, a bar (striped and moving while the percent is unknown) and an optional detail line.
const TestProgress = ({ label, percent, progress, startedAt, noun, testId }) => {
  useTick(Boolean(startedAt));
  const detail = progress || startedAt ? progressDetail(progress, { noun, startedAt }) : '';
  const indeterminate = percent == null;
  return (
    <div className='dg-test__progress' data-testid={testId}>
      <div className='dg-test__progress-head'>
        <span data-testid={`${testId}-label`}>{label}</span>
        {!indeterminate && <span className='dg-num' data-testid={`${testId}-percent`}>{percent}%</span>}
      </div>
      <ProgressBar className='dg-test__bar' variant='primary' now={indeterminate ? 100 : percent}
        animated={indeterminate} striped={indeterminate} label={label} srOnly data-testid={`${testId}-bar`} />
      {detail && <div className='dg-muted dg-test__progress-detail' data-testid={`${testId}-detail`}>{detail}</div>}
    </div>
  );
};

// The last test's answer (contract 3.0): the isolation report — findings, every object with its rows split
// rule / integrity and each rule's values sent → rows matched, why an object got 0, and what the list needs
// with the walk's counts. A failed walk still shows its plan (no counts).
const TestResult = ({ status, noun, keyTable, onFix }) => {
  if (!status || !status.state || status.state === 'NONE') {
    return <div className='dg-muted dg-note' data-testid='dg-test-none'>Not tested since this data graph last changed.</div>;
  }
  if (status.state === 'RUNNING') {
    // No report while it runs (a large schema's is megabytes): the worker's progress instead.
    const progress = status.progress || null;
    return (
      <TestProgress testId='dg-test-running' label={phaseLabel(progress && progress.phase)} percent={progressPercent(progress)}
        progress={progress} startedAt={status.startedAt} noun={noun} />
    );
  }
  const report = status.report || null;
  const probe = status.probe || null;
  const walked = status.state === 'SUCCEEDED';
  const start = (report && report.startTable) || (probe && probe.startTable) || keyTable;
  const seeds = probe && probe.seeds;
  return (
    <div className='mt-2' data-testid='dg-test-result'>
      {status.state === 'FAILED' && <div className='dg-error' data-testid='dg-test-failed'>Test failed: {status.message || 'no details'}</div>}
      {walked && seeds && (
        <div className='dg-note' data-testid='dg-test-seeds'>
          {requestLabel(status.request) && <span className='dg-muted'>{requestLabel(status.request)} · </span>}
          {fmtNum(seeds.found)} of {fmtNum(seeds.requested)} seeds found on {start} · tested {fmtWhen(status.finishedAt)}
        </div>
      )}
      <ReportWarnings warnings={probe && probe.warnings} testId='dg-test-warnings' />
      {report && <IsolationReport report={report} noun={noun} limit={LIST_LIMIT} testId='dg-test-report' onFix={onFix} />}
    </div>
  );
};

// A data graph's Test: plan it from its key table — exactly as a subset whose parent uses it would —
// and walk it for a few records (Any: 10, Where clause: the first 10 matches, ID in: the first 100
// ids). Nothing is staged. It answers which objects come along and which do not, before anyone
// builds a subset. It informs and never blocks: a subset, its preview and its runs are not gated
// on it. The platform keeps the last result on the graph and clears it whenever the graph changes,
// so it is re-read whenever `graph` is replaced by a save. READONLY accounts see the last result,
// not the form (every non-GET is refused for them). Progress (2026-11-02): "Planning…" while the POST
// plans and builds the report, then the worker's progress while RUNNING (re-read every 3 s, cheap now
// that a running test carries no report); the result sections render once the test has finished.
// The panel folds to its head; folded, it still polls and the head says what the test is doing.
// `dirty`: the page's draft has unsaved settings — the Test runs the SAVED graph, so it waits for a save.
// `onStarted`: a test was accepted (the platform wrote the plan's derived rules onto the graph: re-read it).
// `onFix(fix)`: the report's fixes change the page's draft.
const GraphTestPanel = ({ graph, engine, canTest, onResult, dirty = false, onStarted, onFix }) => {
  const [open, setOpen] = useState(true);
  const [form, setForm] = useState({ mode: 'ANY', where: '', seedsText: '' });
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  const graphId = graph ? graph.id : null;
  // Parsed once per edit: a pasted list can be long.
  const seeds = useMemo(() => uniqueSeeds(parseSeeds(form.seedsText)), [form.seedsText]);

  const refresh = useCallback(async () => {
    if (graphId == null) return;
    try {
      const s = await getGraphTest(graphId);
      if (alive.current) setStatus(s);
    } catch (e) { /* keep the last answer */ }
  }, [graphId]);
  useEffect(() => { refresh(); }, [refresh, graph]);
  // The settings panel shows the last Test's counts beside each rule.
  useEffect(() => { if (onResult) onResult(status); }, [status, onResult]);
  const running = Boolean(status && status.state === 'RUNNING');
  usePolling(refresh, POLL_MS, running);

  // React 16 pools the event: read the value now, not inside the updater.
  const set = (k) => (e) => { const { value } = e.target; setForm((f) => ({ ...f, [k]: value })); setError(null); };
  const problem = dirty ? 'Save the settings first — the Test runs the saved data graph.' : testProblem(graph, form, seeds);
  const lang = whereLanguage(engine);
  const noun = reportNoun(engine);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = { mode: form.mode };
      if (form.mode === 'WHERE') body.where = form.where.trim();
      if (form.mode === 'SEEDS') body.seeds = seeds.ids;
      const res = await startGraphTest(graphId, body);
      if (!alive.current) return;
      if (res && res.state) setStatus(res); else await refresh();
      if (onStarted) onStarted();
    } catch (e) {
      if (alive.current) setError(apiErrorText(e, 'Could not start the test'));
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const help = form.mode === 'SEEDS'
    ? `The first ${TESTED_IDS} ids are tested.`
    : form.mode === 'WHERE'
      ? `The first ${SAMPLE_RECORDS} matching records are tested.`
      : `Any pulls just enough records (${SAMPLE_RECORDS}) to check the object list — it is not a full subset.`;

  const folded = running || busy
    ? 'testing…'
    : status && status.state === 'SUCCEEDED' ? `last tested ${fmtWhen(status.finishedAt)}`
      : status && status.state === 'FAILED' ? 'the last test failed' : null;

  return (
    <div className='dg-panel mb-3' data-testid='dg-test'>
      <button type='button' className='dg-panel__head dg-panel__head--toggle' aria-expanded={open}
        onClick={() => setOpen(!open)} data-testid='dg-test-toggle'>
        <div>
          <div className='dg-panel__title'>
            <span className='dg-caret'>{open ? '▾' : '▸'}</span> Test
            {!open && folded && <span className='dg-muted' data-testid='dg-test-folded'> · {folded}</span>}
          </div>
          <div className='dg-muted'>Walk this data graph from its key table for a few records: what each rule reaches, what is isolated, and what a provision would need.</div>
        </div>
      </button>
      {open && canTest && (
        <>
          <div className='dg-test__form'>
            <label className='dg-config__field'>Records
              <select className='dg-input' value={form.mode} onChange={set('mode')} data-testid='dg-test-mode'>
                {TEST_MODES.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
              </select>
            </label>
            {form.mode === 'WHERE' && (
              <label className='dg-config__field'>{lang.label}
                <input className='dg-input' value={form.where} onChange={set('where')} placeholder={lang.placeholder}
                  data-testid='dg-test-where' />
              </label>
            )}
            {form.mode === 'SEEDS' && (
              <label className='dg-config__field'>Ids{graph && graph.keyColumn ? ` (${graph.keyColumn})` : ''}
                <textarea className='dg-input dg-filter__text' rows={3} value={form.seedsText} onChange={set('seedsText')}
                  placeholder='one per line or comma-separated' data-testid='dg-test-seeds-text' />
                <span className='dg-muted' data-testid='dg-test-seeds-count'>
                  {fmtNum(seeds.ids.length)} ids{seeds.duplicates > 0 ? ` · ${fmtNum(seeds.duplicates)} duplicates dropped` : ''}
                </span>
              </label>
            )}
          </div>
          <div className='dg-muted dg-note' data-testid='dg-test-help'>{help}</div>
          <div className='dg-config__actions'>
            <Button size='sm' onClick={run} disabled={busy || running || Boolean(problem)} data-testid='dg-test-run'>
              {running || busy ? 'Testing…' : 'Test'}
            </Button>
            {problem && <span className='dg-muted' data-testid='dg-test-problem'>{problem}</span>}
          </div>
          {error && <div className='dg-error' data-testid='dg-test-error'>{error}</div>}
        </>
      )}
      {open && (busy
        ? <TestProgress testId='dg-test-planning' label='Planning…' />
        : <TestResult status={status} noun={noun} keyTable={graph && graph.keyTable} onFix={onFix} />)}
    </div>
  );
};

export default GraphTestPanel;
